import React, { useState } from 'react';
import { Button, Form, Card, Row, Col, InputGroup } from 'react-bootstrap';
import { FaPlus, FaTrash, FaTimes, FaSave } from 'react-icons/fa';
import { Flow, FlowStep, FlowAction, questionToVariableName } from './types';
import { Icon } from '../../../../../components/common/Icon';

interface FlowEditorProps {
    initialFlow?: Flow | null;
    onSave: (flow: Flow) => void;
    onCancel: () => void;
}

const defaultFlow: Flow = {
    id: '',
    name: '',
    trigger_keywords: [],
    is_active: true,
    steps: [],
    actions: [],
    completion_message_success: 'Thank you! We have received your information.',
    completion_message_failure: 'Something went wrong. Please try again later.'
};

const defaultCompletionSuccess = 'Thank you! We have received your information.';
const defaultCompletionFailure = 'Something went wrong. Please try again later.';

const FlowEditor: React.FC<FlowEditorProps> = ({ initialFlow, onSave, onCancel }) => {
    const initial = (() => {
        if (!initialFlow) return { ...defaultFlow, id: Date.now().toString() };
        const f = { ...initialFlow };
        f.completion_message_success = initialFlow.completion_message_success ?? initialFlow.completion_message ?? defaultCompletionSuccess;
        f.completion_message_failure = initialFlow.completion_message_failure ?? defaultCompletionFailure;
        // Migrate flow-level completion to first action if actions exist but have no completion
        if (f.actions?.length > 0 && f.actions[0] && (f.actions[0].completion_message_success == null && f.actions[0].completion_message_failure == null)) {
            f.actions = [...f.actions];
            f.actions[0] = {
                ...f.actions[0],
                completion_message_success: f.actions[0].completion_message_success ?? f.completion_message_success ?? defaultCompletionSuccess,
                completion_message_failure: f.actions[0].completion_message_failure ?? f.completion_message_failure ?? defaultCompletionFailure
            };
        }
        return f;
    })();
    const [flow, setFlow] = useState<Flow>(initial);
    const [showTriggerInput, setShowTriggerInput] = useState(false);
    const [triggerInputValue, setTriggerInputValue] = useState('');

    const handleAddTrigger = () => {
        setShowTriggerInput(true);
        setTriggerInputValue('');
    };

    const submitTrigger = () => {
        if (triggerInputValue.trim()) {
            setFlow(prev => ({
                ...prev,
                trigger_keywords: [...prev.trigger_keywords, triggerInputValue.trim()]
            }));
            setTriggerInputValue('');
            setShowTriggerInput(false);
        }
    };

    const cancelTriggerInput = () => {
        setShowTriggerInput(false);
        setTriggerInputValue('');
    };

    const removeKeyword = (index: number) => {
        setFlow(prev => ({
            ...prev,
            trigger_keywords: prev.trigger_keywords.filter((_, i) => i !== index)
        }));
    };

    const addStep = () => {
        const newStep: FlowStep = {
            id: Date.now().toString(),
            question: '',
            variable: '',
            type: 'text'
        };
        setFlow(prev => ({ ...prev, steps: [...prev.steps, newStep] }));
    };

    const updateStep = (index: number, field: keyof FlowStep, value: any) => {
        const newSteps = [...flow.steps];
        const updated = { ...newSteps[index], [field]: value };
        if (field === 'question') updated.variable = questionToVariableName(value);
        newSteps[index] = updated;
        setFlow(prev => ({ ...prev, steps: newSteps }));
    };

    const removeStep = (index: number) => {
        setFlow(prev => ({
            ...prev,
            steps: prev.steps.filter((_, i) => i !== index)
        }));
    };

    const addAction = () => {
        setFlow(prev => ({
            ...prev,
            actions: [...prev.actions, {
                id: Date.now().toString(),
                type: 'email',
                recipient_email: '',
                subject: 'New AI Agent Submission',
                completion_message_success: defaultCompletionSuccess,
                completion_message_failure: defaultCompletionFailure
            }]
        }));
    };

    const updateAction = (index: number, field: keyof FlowAction, value: any) => {
        const newActions = [...flow.actions];
        newActions[index] = { ...newActions[index], [field]: value };
        setFlow(prev => ({ ...prev, actions: newActions }));
    };

    const removeAction = (index: number) => {
        setFlow(prev => ({
            ...prev,
            actions: prev.actions.filter((_, i) => i !== index)
        }));
    };

    const handleSave = () => {
        if (!flow.name) return alert("Flow name is required");
        if (flow.steps.length === 0) return alert("Add at least one step");
        // Backend uses flow-level completion; set from first action when present
        const out: Flow = { ...flow };
        if (flow.actions?.length > 0) {
            out.completion_message_success = flow.actions[0].completion_message_success ?? flow.completion_message_success ?? defaultCompletionSuccess;
            out.completion_message_failure = flow.actions[0].completion_message_failure ?? flow.completion_message_failure ?? defaultCompletionFailure;
        }
        onSave(out);
    };

    return (
        <div className="flow-editor animate-fade-in">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4>{initialFlow ? 'Edit Flow' : 'Create New Flow'}</h4>
                <div className="d-flex gap-2">
                    <Button onClick={onCancel} className="chatbots-actions-cancel">Cancel</Button>
                    <Button onClick={handleSave} className="chatbots-actions-save"><Icon icon={FaSave} className="me-1" /> Save Flow</Button>
                </div>
            </div>

            {/* 1. Flow Name */}
            <Card className="mb-4 shadow-sm border-0">
                <Card.Body>
                    <Form.Group className="mb-0">
                        <Form.Label>Flow Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={flow.name}
                            onChange={e => setFlow({ ...flow, name: e.target.value })}
                            placeholder="e.g. Lead Qualification"
                        />
                    </Form.Group>
                </Card.Body>
            </Card>

            {/* 2. Trigger Keywords | Steps | Actions — 3 equal columns */}
            <Row>
                <Col lg={4}>
                    <Card className="mb-4 shadow-sm border-0 h-100 flow-classic-card flow-classic-card-trigger">
                        <Card.Header className="flow-classic-header flow-classic-header-trigger py-3 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0 fw-bold">Trigger (Keywords)</h5>
                            <Button size="sm" variant="light" className="flow-classic-header-btn" onClick={handleAddTrigger}><FaPlus /> Add Trigger</Button>
                        </Card.Header>
                        <Card.Body>
                            {showTriggerInput && (
                                <div className="flow-trigger-inline-add mb-2">
                                    <InputGroup>
                                        <Form.Control
                                            size="sm"
                                            placeholder="Keyword or phrase..."
                                            value={triggerInputValue}
                                            onChange={e => setTriggerInputValue(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') submitTrigger(); if (e.key === 'Escape') cancelTriggerInput(); }}
                                            autoFocus
                                        />
                                        <Button variant="outline-primary" size="sm" onClick={submitTrigger} disabled={!triggerInputValue.trim()}>Add</Button>
                                        <Button variant="outline-secondary" size="sm" onClick={cancelTriggerInput}>Cancel</Button>
                                    </InputGroup>
                                </div>
                            )}
                            {flow.trigger_keywords.length === 0 && !showTriggerInput && <p className="text-muted text-center py-3 small">No triggers. Click &quot;Add Trigger&quot; to add keywords or phrases.</p>}
                            {flow.trigger_keywords.map((kw, i) => (
                                <div key={i} className="flow-trigger-item mb-2 p-2 border rounded bg-light d-flex justify-content-between align-items-center">
                                    <span className="small text-dark">{kw}</span>
                                    <Button size="sm" variant="link" className="text-danger p-0 min-w-auto" onClick={() => removeKeyword(i)}><FaTrash /></Button>
                                </div>
                            ))}
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={4}>
                    <Card className="mb-4 shadow-sm border-0 h-100 flow-classic-card flow-classic-card-step">
                        <Card.Header className="flow-classic-header flow-classic-header-step py-3 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0 fw-bold text-white">Steps (Questions)</h5>
                            <Button size="sm" variant="light" className="flow-classic-header-btn" onClick={addStep}><FaPlus /> Add Step</Button>
                        </Card.Header>
                        <Card.Body>
                            {flow.steps.length === 0 && <p className="text-muted text-center py-4 small">No steps defined. Add questions to ask the user.</p>}
                            {flow.steps.map((step, idx) => (
                                <div key={step.id} className="flow-step-item mb-3 p-3 border rounded bg-light position-relative">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="fw-bold text-muted">Step {idx + 1}</span>
                                        <Button size="sm" variant="link" className="text-danger p-0" onClick={() => removeStep(idx)}><FaTrash /></Button>
                                    </div>
                                    <Form.Control
                                        size="sm"
                                        placeholder="Question (e.g. What is your email?)"
                                        value={step.question}
                                        onChange={e => updateStep(idx, 'question', e.target.value)}
                                    />
                                </div>
                            ))}
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={4}>
                    <Card className="mb-4 shadow-sm border-0 h-100 flow-classic-card flow-classic-card-action">
                        <Card.Header className="flow-classic-header flow-classic-header-action py-3 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0 fw-bold text-white">Actions (On Complete)</h5>
                            <Button size="sm" variant="light" className="flow-classic-header-btn" onClick={addAction}><FaPlus /> Add Action</Button>
                        </Card.Header>
                        <Card.Body>
                            {flow.actions.length === 0 && <p className="text-muted text-center py-4">No actions defined. Collected data will only be stored.</p>}
                            {flow.actions.map((action, idx) => (
                                <div key={action.id} className="flow-action-item mb-3 p-3 border rounded border-start border-success position-relative">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="fw-bold text-success">Action {idx + 1}: {action.type.toUpperCase()}</span>
                                        <Button size="sm" variant="link" className="text-danger p-0" onClick={() => removeAction(idx)}><FaTrash /></Button>
                                    </div>
                                    {action.type === 'email' && (
                                        <div className="d-flex flex-column gap-2 mb-3">
                                            <Form.Control
                                                size="sm"
                                                placeholder="Recipient Email"
                                                value={action.recipient_email ?? ''}
                                                onChange={e => updateAction(idx, 'recipient_email', e.target.value)}
                                            />
                                            <Form.Control
                                                size="sm"
                                                placeholder="Email Subject"
                                                value={action.subject ?? ''}
                                                onChange={e => updateAction(idx, 'subject', e.target.value)}
                                            />
                                        </div>
                                    )}
                                    <div className="flow-action-completion mt-2 pt-2 border-top border-light">
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <span className="flow-completion-label success">Success</span>
                                            <Form.Control
                                                size="sm"
                                                className="form-control-sm"
                                                value={action.completion_message_success ?? ''}
                                                onChange={e => updateAction(idx, 'completion_message_success', e.target.value)}
                                                placeholder="Message on success..."
                                            />
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="flow-completion-label failure">Failure</span>
                                            <Form.Control
                                                size="sm"
                                                className="form-control-sm"
                                                value={action.completion_message_failure ?? ''}
                                                onChange={e => updateAction(idx, 'completion_message_failure', e.target.value)}
                                                placeholder="Message on failure..."
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default FlowEditor;
